import Point from './point';
import Line from './line';

/**
 * A Bounds instance represents a rectangle in the two dimensional
 * editor space. This rectangle is defined by its top-left and bottom-right corners.
 * Both points are represented by a instance of Point.
 *
 *     var bounds = new Bounds(
 *         new Point(100, 200),
 *         new Point(600, 800)
 *     );
 */
export default class Bounds {
    /**
     * @param {Point} point One or more which define the outer edges of the bounds instance
     * @param {...Point} args
     */
    constructor(point, ...args) {
        if (!(point instanceof Point)) {
            throw new Error(`Expecting instance of Point, but ${typeof (point)} was given.`);
        }

        this.topLeft = new Point(point.x, point.y);
        this.bottomRight = new Point(point.x, point.y);

        args.forEach(arg => this.extend(arg));
    }

    /**
     * Returns a new instance of Point which contains the top-left corner
     * of the bounding box.
     *
     * @return {Point} Point of top-left corner
     */
    getTopLeft() {
        return new Point(this.topLeft.x, this.topLeft.y);
    }

    /**
     * Returns a new instance of Point which contains the top-center coordinates
     * of the bounding box.
     *
     * @return {Point} Point of top-left corner
     */
    getTopCenter() {
        return new Point(this.getCenter().x, this.topLeft.y);
    }

    /**
     * Returns a new instance of Point which contains the top-right corner
     * of the bounding box.
     *
     * @return {Point} Point of top-left corner
     */
    getTopRight() {
        return new Point(this.bottomRight.x, this.topLeft.y);
    }

    /**
     * Returns a new instance of Point which contains the center-left coordinates
     * of the bounding box.
     *
     * @return {Point} Point of top-left corner
     */
    getCenterLeft() {
        return new Point(this.topLeft.x, this.getCenter().y);
    }

    /**
     * Returns a new instance of Point which contains the center coordinates
     * of the bounding box.
     *
     * @return {Point} Point of top-left corner
     */
    getCenter() {
        return new Point(
            ((this.bottomRight.x - this.topLeft.x) / 2) + this.topLeft.x,
            ((this.bottomRight.y - this.topLeft.y) / 2) + this.topLeft.y,
        );
    }

    /**
     * Returns a new instance of Point which contains the center-right coordinates
     * of the bounding box.
     *
     * @return {Point} Point of top-left corner
     */
    getCenterRight() {
        return new Point(this.bottomRight.x, this.getCenter().y);
    }

    /**
     * Returns a new instance of Point which contains the bottom-left
     * corner of the bounding box.
     *
     * @return {Point} Point of bottom-right corner
     */
    getBottomLeft() {
        return new Point(this.topLeft.x, this.bottomRight.y);
    }

    /**
     * Returns a new instance of Point which contains the bottom-center
     * coordinates of the bounding box.
     *
     * @return {Point} Point of bottom-right corner
     */
    getBottomCenter() {
        return new Point(this.getCenter().x, this.bottomRight.y);
    }

    /**
     * Returns a new instance of Point which contains the bottom-right
     * corner of the bounding box.
     *
     * @return {Point} Point of bottom-right corner
     */
    getBottomRight() {
        return new Point(this.bottomRight.x, this.bottomRight.y);
    }

    /**
     * Returns width of the current bounding box
     *
     * @return {number} Width of bounding box
     */
    getWidth() {
        return this.bottomRight.x - this.topLeft.x;
    }

    /**
     * Returns height of the current bounding box
     *
     * @return {number} Width of bounding box
     */
    getHeight() {
        return this.bottomRight.y - this.topLeft.y;
    }

    /**
     * Returns true if bounds contains the given point
     *
     * @param  {Point|Line|Bounds} target Point or Line or Bounds
     * @return {boolean} True if target in bounds
     */
    contains(target) {
        if (target instanceof Point) {
            return target.x >= this.topLeft.x && target.x <= this.bottomRight.x
                && target.y >= this.topLeft.y && target.y <= this.bottomRight.y;
        }

        if (target instanceof Line) {
            return this.contains(target.startPoint) && this.contains(target.endPoint);
        }

        if (target instanceof Bounds) {
            return this.contains(target.topLeft) && this.contains(target.bottomRight);
        }

        throw new Error('Unknown argument for contains()');
    }

    /**
     * Returns true if given bounds is equal to this bounds
     *
     * @param  {Bounds} bounds Bounds to check against
     * @param  {number} [delta=0.0001] Delta which defines the check tolerance
     * @return {boolean}
     */
    equal(bounds, delta = 0.0001) {
        return bounds.topLeft.equal(this.topLeft, delta) && bounds.bottomRight.equal(this.bottomRight, delta);
    }

    /**
     * Extends bounds to the given point
     *
     * @param {Point} point Point to extend bounds to
     * @return {Bounds} this
     * @chainable
     */
    extend(point) {
        if (!(point instanceof Point)) {
            throw new Error(`Expecting instance of Point, but ${point} was given.`);
        }

        this.topLeft.x = Math.min(this.topLeft.x, point.x);
        this.topLeft.y = Math.min(this.topLeft.y, point.y);
        this.bottomRight.x = Math.max(this.bottomRight.x, point.x);
        this.bottomRight.y = Math.max(this.bottomRight.y, point.y);

        return this;
    }

    /**
     * Check if this bounds share any point with the given bounds
     *
     * @param  {Bounds} other Bounds to check against
     * @return {boolean} True if bounds intersects
     */
    intersect(other) {
        return other.bottomRight.x > this.topLeft.x
            && other.bottomRight.y > this.topLeft.y
            && other.topLeft.x < this.bottomRight.x
            && other.topLeft.y < this.bottomRight.y;
    }

    /**
     * Merge bounds with other bounds.
     *
     * @param  {Bounds} other Bounds to merge
     * @return {Bounds} Merged bounds
     */
    merge(other) {
        const bounds = new Bounds(this.topLeft, this.bottomRight);
        bounds.extend(other.topLeft);
        bounds.extend(other.bottomRight);

        return bounds;
    }

    /**
     * Merge and intersect bounds with other bounds.
     *
     * @param  {Bounds} other Bounds to merge and intersect
     * @return {Bounds} Merged bounds
     */
    mergeIntersect(other) {
        if (!this.intersect(other)) throw new Error('Bounds are not intersected');

        const x1 = this.topLeft.x > other.topLeft.x ? this.topLeft.x : other.topLeft.x;
        const y1 = this.topLeft.y > other.topLeft.y ? this.topLeft.y : other.topLeft.y;
        const x2 = this.bottomRight.y < other.bottomRight.y ? this.bottomRight.y : other.bottomRight.y;
        const y2 = this.bottomRight.y < other.bottomRight.y ? this.bottomRight.y : other.bottomRight.y;

        return new Bounds(new Point(x1, y1), new Point(x2, y2));
    }

    /**
     * Transform this bounds object.
     *
     * @param {Matrix} matrix
     *
     * @return {Bounds} this
     *
     * @chainable
     */
    transform(matrix) {
        const bounds = new Bounds(this.getTopLeft().transform(matrix));
        bounds.extend(this.getTopRight().transform(matrix));
        bounds.extend(this.getBottomRight().transform(matrix));
        bounds.extend(this.getBottomLeft().transform(matrix));

        this.topLeft = bounds.topLeft;
        this.bottomRight = bounds.bottomRight;

        return this;
    }

    /**
     * Move bounds by distance
     *
     * @param {Distance} distance
     * @return {Bounds}
     * @chainable
     */
    move(distance) {
        this.topLeft.x += distance.point.x;
        this.topLeft.y += distance.point.y;
        this.bottomRight.x += distance.point.x;
        this.bottomRight.y += distance.point.y;

        return this;
    }

    /**
     * Move bounds to new top left position and keep size
     *
     * @param {Point} point
     * @return {Bounds}
     * @chainable
     */
    moveTo(point) {
        return new Bounds(point, new Point(point.x + this.getWidth(), point.y + this.getHeight()));
    }

    /**
     * Move bounds to new top left position and keep size
     *
     * @return {Bounds}
     * @chainable
     */
    moveToNull() {
        return this.moveTo(new Point(0, 0));
    }

    /**
     * Clone this bounds object.
     *
     * @return {Bounds}
     */
    clone() {
        return new Bounds(this.topLeft)
            .extend(this.bottomRight);
    }

    /**
     * Return an array containing x and y
     *
     * This can be used to serialize and persist the point.
     * The name of this method is a bit confusing, because it
     * conforms to JavaScript's JSON API.
     *
     * See [Mozilla Developer Network – toJSON() method][1]
     *
     * [1]: https://developer.mozilla.org/en-US/docs/JSON#toJSON()_method
     *
     * @return {Array} Array which contains the array representation of top-left and bottom-right
     */
    toJSON() {
        return [this.topLeft.toJSON(), this.bottomRight.toJSON()];
    }
}
